import React from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Zap, TrendingUp, TrendingDown, Minus, Leaf, Activity, AlertTriangle, Info, Battery, Sun } from "lucide-react";

const FEEDERS = [
  { name: "Main HVAC Feeder",   panel: "MDB-01",   kw: 142, kwh: 1840, pf: 0.96, status: "Normal",    trend: "up"   },
  { name: "Lighting DB (GF-L2)", panel: "LDB-02",  kw: 38,  kwh: 420,  pf: 0.91, status: "Normal",    trend: "flat" },
  { name: "Food Court Feeder",  panel: "MDB-03",   kw: 67,  kwh: 890,  pf: 0.88, status: "Watch",     trend: "up"   },
  { name: "Parking Lighting",   panel: "LDB-04",   kw: 12,  kwh: 156,  pf: 0.92, status: "Normal",    trend: "flat" },
  { name: "Escalators & Lifts", panel: "MDB-05",   kw: 28,  kwh: 334,  pf: 0.95, status: "Normal",    trend: "down" },
  { name: "DG Set (Standby)",   panel: "DG-01",    kw: 0,   kwh: 0,    pf: null, status: "Standby",   trend: "flat" },
  { name: "Solar Rooftop",      panel: "SOLAR-01", kw: -31, kwh: -286, pf: null, status: "Generating", trend: "down" },
];

const HOURLY = Array.from({ length: 24 }, (_, h) => {
  const base = 100 + Math.sin((h - 6) / 3.5) * 150 + (h > 10 && h < 20 ? 120 : 0);
  return {
    hour: `${String(h).padStart(2, "0")}:00`,
    today: Math.round(base + Math.random() * 30),
    yesterday: Math.round(base + Math.random() * 40 - 10),
  };
});

const PIE = [
  { name: "HVAC",        value: 45, color: "#ff6b35" },
  { name: "Food Court",  value: 21, color: "#f59e0b" },
  { name: "Lighting",    value: 18, color: "#38bdf8" },
  { name: "Lifts & Esc", value: 11, color: "#a78bfa" },
  { name: "Others",      value: 5,  color: "#64748b" },
];

const TONE_MAP = {
  Normal:     { bg: "bg-emerald-500/15", text: "text-emerald-300", dot: "🟢" },
  Watch:      { bg: "bg-amber-500/15",   text: "text-amber-300",   dot: "🟡" },
  Standby:    { bg: "bg-slate-500/15",   text: "text-slate-300",   dot: "⚪" },
  Generating: { bg: "bg-emerald-500/15", text: "text-emerald-300", dot: "🟢" },
};

function Kpi({ icon: Icon, label, value, unit, tone = "blue", sub }) {
  const g = {
    green:  "from-emerald-400 to-emerald-600",
    amber:  "from-amber-400 to-orange-500",
    red:    "from-red-500 to-pink-600",
    blue:   "from-sky-400 to-indigo-500",
    purple: "from-purple-500 to-indigo-500",
    orange: "from-orange-500 to-pink-500",
  };
  return (
    <div className="glass rounded-lg p-5">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${g[tone]} flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-gray-900" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      </div>
      <div className="mt-3">
        <span className="text-3xl font-extrabold tracking-tight">{value}</span>
        {unit && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

const TrendIcon = ({ t }) => t === "up" ? <TrendingUp className="w-3.5 h-3.5 text-red-400" strokeWidth={2.5} /> : t === "down" ? <TrendingDown className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} /> : <Minus className="w-3.5 h-3.5 text-gray-500" strokeWidth={2.5} />;

export default function EnergyDashboard() {
  const pfTone = (pf) => pf === null ? "text-slate-400" : pf >= 0.90 ? "text-emerald-300" : pf >= 0.85 ? "text-amber-300" : "text-red-300";

  return (
    <div className="space-y-6" data-testid="energy-dashboard-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
              <Zap className="w-8 h-8 text-amber-400" strokeWidth={2.5} /> Energy Meter Dashboard
            </h1>
            <span className="px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest">Live</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Real-time power consumption & quality · 7 feeders</p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest bg-amber-500/10 text-amber-300 border border-amber-500/20 font-bold self-start">BEE / ECBC Compliance View</span>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi icon={Activity}  label="Today" value="4,280" unit="kWh" tone="orange" />
        <Kpi icon={Zap}       label="This Month" value="1,24,600" unit="kWh" tone="purple" />
        <Kpi icon={Battery}   label="Current Demand" value="318" unit="kW" tone="blue" sub="Peak today: 412 kW" />
        <Kpi icon={TrendingUp} label="Power Factor" value="0.94" tone="green" sub="Target ≥ 0.90" />
        <Kpi icon={Leaf}      label="Carbon Today" value="51.4" unit="kg CO₂" tone="green" />
      </div>

      {/* FEEDER TABLE */}
      <div className="glass rounded-lg overflow-hidden" data-testid="energy-feeder-table">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-bold text-lg">Feeder / Panel Consumption</h2>
          <span className="text-[11px] text-gray-500">Auto-refresh 15s</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">Feeder</th>
                <th className="text-left py-3">Panel</th>
                <th className="text-right py-3">kW Now</th>
                <th className="text-right py-3">kWh Today</th>
                <th className="text-right py-3">PF</th>
                <th className="text-center py-3">Status</th>
                <th className="text-center px-5 py-3">Trend</th>
              </tr>
            </thead>
            <tbody>
              {FEEDERS.map((f) => {
                const t = TONE_MAP[f.status] || TONE_MAP.Normal;
                return (
                  <tr key={f.panel} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`feeder-${f.panel}`}>
                    <td className="px-5 py-3 font-semibold flex items-center gap-2">
                      {f.panel.startsWith("SOLAR") && <Sun className="w-3.5 h-3.5 text-amber-400" strokeWidth={2.5} />}
                      {f.name}
                    </td>
                    <td className="py-3 text-gray-500 font-mono text-xs">{f.panel}</td>
                    <td className={`py-3 text-right font-bold tabular-nums ${f.kw < 0 ? "text-emerald-300" : ""}`}>{f.kw} kW</td>
                    <td className={`py-3 text-right tabular-nums ${f.kwh < 0 ? "text-emerald-300" : "text-slate-200"}`}>{f.kwh.toLocaleString("en-IN")}</td>
                    <td className={`py-3 text-right tabular-nums ${pfTone(f.pf)}`}>{f.pf === null ? "—" : f.pf.toFixed(2)}</td>
                    <td className="py-3 text-center">
                      <span className={`inline-block px-2.5 py-1 rounded-full ${t.bg} ${t.text} text-[10px] uppercase tracking-widest font-bold`}>{f.status}</span>
                    </td>
                    <td className="px-5 py-3 text-center"><TrendIcon t={f.trend} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass rounded-lg p-5">
          <h3 className="font-semibold mb-3">Hourly kWh — Today vs Yesterday</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={HOURLY}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#64748b" fontSize={10} interval={2} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              <Legend />
              <Line type="monotone" dataKey="today" stroke="#ff6b35" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="yesterday" stroke="#64748b" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="glass rounded-lg p-5">
          <h3 className="font-semibold mb-3">Energy Split — Today</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={PIE} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={95} paddingAngle={2}>
                {PIE.map((p) => <Cell key={p.name} fill={p.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} formatter={(v) => `${v}%`} />
              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ALERTS */}
      <div className="glass rounded-lg p-5" data-testid="energy-alerts">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={2.5} /> Active Energy Alerts</h3>
        <div className="space-y-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <span className="px-2 py-0.5 rounded-full bg-orange-500/25 text-orange-200 text-[10px] uppercase tracking-widest font-bold">High</span>
            <div className="flex-1">
              <div className="font-bold text-gray-900">Food Court Feeder — Power Factor at 0.88</div>
              <div className="text-xs text-gray-500 mt-0.5">Below BEE recommended 0.90 · Check capacitor bank · MDB-03</div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-sky-500/10 border border-sky-500/20">
            <Info className="w-4 h-4 text-sky-300 shrink-0 mt-0.5" strokeWidth={2.5} />
            <div className="flex-1">
              <div className="font-bold text-gray-900">Solar rooftop generated 286 kWh today</div>
              <div className="text-xs text-gray-500 mt-0.5">Estimated savings ₹2,002 at ₹7/unit · Carbon offset 228 kg CO₂</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
